import { NotificationBell } from "@expert/common-ui/assets";
import { getLogger } from "@expert/logging";
import { Badge, Box, Button, Group, Menu, Text, Tooltip } from "@mantine/core";
import { type JSX, useState } from "react";

import { useOutageNotifications } from "../../hooks";
import { formatServiceName } from "../../utils";
import classes from "./NotificationCenter.module.css";

const logger = getLogger({ module: "NotificationCenterForOutages", supportTeam: "solve" });

export function NotificationCenter(): JSX.Element {
    const [opened, setOpened] = useState(false);
    const { outageAlerts, unreadCount, markAllAsRead } = useOutageNotifications(logger);

    const handleOpenMenu = () => {
        setOpened(true);
    };

    const handleCloseMenu = () => {
        if (opened) {
            markAllAsRead();
        }
    };

    const getStatusLabel = (status: string) => {
        return status === "active" ? "Ongoing" : "Resolved";
    };

    return (
        <Menu
            width={322}
            shadow="sm"
            zIndex="var(--mantine-priority-highest)"
            opened={opened}
            onChange={setOpened}
            radius="var(--mantine-spacing-sm)"
            closeOnClickOutside
            position="bottom-start"
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            closeOnItemClick={false}
        >
            <Menu.Target>
                <Tooltip label="Notifications" zIndex="var(--mantine-priority-highest)" disabled={opened}>
                    <Button
                        variant="subtle"
                        color="gray"
                        className={classes.notificationButton}
                        data-menu-open={opened}
                        data-testid="notification-center"
                        id="notification-center-button"
                        size="xs"
                        mr={0}
                    >
                        <NotificationBell color="var(--mantine-color-white)" />
                        {unreadCount > 0 && (
                            <Badge size="xs" color="red" variant="filled" className={classes.notificationBadge} />
                        )}
                    </Button>
                </Tooltip>
            </Menu.Target>
            <Menu.Dropdown p="0" className={classes.notificationDropdown}>
                <Box className={classes.notificationHeader}>
                    <Text fw={400} size="sm">
                        {"notifications".toUpperCase()}
                    </Text>
                </Box>
                {outageAlerts.length > 0 ? (
                    outageAlerts.map((alert) => (
                        <Menu.Item key={alert.id} className={classes.outageAlert}>
                            <Group justify="space-between" wrap="nowrap">
                                <Text size="sm" fw={400}>
                                    {formatServiceName(alert.serviceName)} service outage
                                </Text>
                                <Box
                                    className={`${classes.statusTag} ${
                                        alert.status === "active" ? classes.activeTag : classes.clearTag
                                    }`}
                                >
                                    <Text size="xs" fw={500}>
                                        {getStatusLabel(alert.status)}
                                    </Text>
                                </Box>
                            </Group>
                            <Text size="sm" c="var(--mantine-color-dark-2)">
                                {alert.timeAgo} - {alert.reportCount} reports
                            </Text>
                        </Menu.Item>
                    ))
                ) : (
                    <Box py="md" mx="md" ta="left">
                        <Text size="sm">No notifications at this time</Text>
                    </Box>
                )}
            </Menu.Dropdown>
        </Menu>
    );
}
